/**
 * Staff Request Form Component
 * Form for creating staff requests with qualification requirements
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Badge } from '@tracekit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAPIClient, listUsers } from '@tracekit/core';
import { useToastStore } from '@tracekit/core';
import {
  UserPlus,
  Award,
  MapPin,
  Briefcase,
  FileText,
  X,
  CheckCircle,
  Info,
} from 'lucide-react';

interface StaffRequestFormProps {
  projectId: string;
  opportunityId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  tenant_assignments?: Array<{
    role_name?: string;
  }>;
}

export function StaffRequestForm({
  projectId,
  opportunityId,
  onSuccess,
  onCancel,
}: StaffRequestFormProps) {
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    required_designations: [] as string[],
    required_expertise: [] as string[],
    min_experience_years: '',
    location_requirements: {
      province: '',
      city: '',
    },
    justification: '',
  });

  // Available designations (from staff data)
  const availableDesignations = [
    'P.Eng', 'P.Geo', 'P.Geo. (Ltd)', 'P.L.Geo', 'R.P.Bio', 'RPBio', 'P.Ag', 'P.Chem', 'PChem',
    'CSAP', 'CSAP (Risk)', 'QPESA', 'EIT', 'GIT', 'EP', 'EPt', 'AScT', 'PMP', 'P.E.', 'P.Biol.',
    'PBD', 'RBTech', 'BC-CESCL', 'NCSO', 'CGA', 'CPA', 'B.Sc.', 'B.Sc.H.', 'B.A.', 'B.A.Sc.',
    'B.ES.', 'B.Eng.', 'B.I.T.', 'BIT', 'B.Tech', 'B.Tech.', 'BBA', 'M.Sc.', 'M.Eng.', 'M.A.Sc.',
    'M.Env.Sc.', 'PhD', 'Dipl. T (Env)', 'Adv.Dip. GIS', 'ADGIS', 'A.Ag'
  ];

  const availableExpertise = [
    'Contaminated Sites Investigation & Remediation',
    'Environmental Assessment',
    'Remediation',
    'Risk Assessment',
    'Environmental Engineering',
    'Biology & Environmental Impact Assessment',
    'Indoor Air Quality & Hazardous Materials',
    'EHS Management & Compliance',
    'Environmental Spill Response',
    'Landfill Management',
    'Forestry',
    'Indigenous Engagement',
  ];

  const provinces = ['BC', 'AB', 'ON', 'QC', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'YT', 'NT', 'NU'];

  // Create staff request mutation
  const createRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const api = getAPIClient();
      const response = await api.post('/api/rfp/staff/requests', {
        project_id: projectId,
        opportunity_id: opportunityId,
        ...data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-requests', projectId] });
      addToast({
        type: 'success',
        title: 'Staff Request Created',
        message: 'Your staff request has been submitted and will be reviewed by an Admin or Admin Tech.',
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        title: 'Failed to Create Request',
        message: error?.response?.data?.detail || error?.message || 'Unknown error occurred',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (formData.required_designations.length === 0 && 
        formData.required_expertise.length === 0 && 
        !formData.min_experience_years) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please specify at least one requirement (designations, expertise, or experience).',
      });
      return;
    }

    if (!formData.justification.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please provide a justification for this staff request.',
      });
      return;
    }

    createRequestMutation.mutate(formData);
  };

  const toggleDesignation = (desig: string) => {
    setFormData(prev => ({
      ...prev,
      required_designations: prev.required_designations.includes(desig)
        ? prev.required_designations.filter(d => d !== desig)
        : [...prev.required_designations, desig],
    }));
  };

  const toggleExpertise = (exp: string) => {
    setFormData(prev => ({
      ...prev,
      required_expertise: prev.required_expertise.includes(exp)
        ? prev.required_expertise.filter(e => e !== exp)
        : [...prev.required_expertise, exp],
    }));
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <UserPlus className="w-6 h-6" />
          Create Staff Request
        </h3>
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Required Designations */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Award className="w-4 h-4" />
            Required Designations
            <Info className="w-4 h-4 text-gray-400" title="Select professional designations required for this role" />
          </label>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {availableDesignations.map(desig => (
                <button
                  key={desig}
                  type="button"
                  onClick={() => toggleDesignation(desig)}
                  className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                    formData.required_designations.includes(desig)
                      ? 'bg-blue-100 border-blue-500 text-blue-800 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {desig}
                </button>
              ))}
            </div>
          </div>
          {formData.required_designations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.required_designations.map(desig => (
                <Badge key={desig} variant="default" className="flex items-center gap-1">
                  {desig}
                  <button
                    type="button"
                    onClick={() => toggleDesignation(desig)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Required Expertise */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Required Expertise Areas
            <Info className="w-4 h-4 text-gray-400" title="Select expertise areas required for this role" />
          </label>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {availableExpertise.map(exp => (
                <button
                  key={exp}
                  type="button"
                  onClick={() => toggleExpertise(exp)}
                  className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                    formData.required_expertise.includes(exp)
                      ? 'bg-blue-100 border-blue-500 text-blue-800 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {exp}
                </button>
              ))}
            </div>
          </div>
          {formData.required_expertise.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.required_expertise.map(exp => (
                <Badge key={exp} variant="default" className="flex items-center gap-1">
                  {exp}
                  <button
                    type="button"
                    onClick={() => toggleExpertise(exp)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Minimum Experience */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Minimum Experience (Years)
          </label>
          <Input
            type="number"
            min="0"
            value={formData.min_experience_years}
            onChange={(e) => setFormData(prev => ({ ...prev, min_experience_years: e.target.value }))}
            placeholder="e.g., 5"
          />
        </div>

        {/* Location Requirements */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Location Requirements
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Province</label>
              <select
                value={formData.location_requirements.province}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location_requirements: { ...prev.location_requirements, province: e.target.value },
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">No preference</option>
                {provinces.map(prov => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">City (Optional)</label>
              <Input
                value={formData.location_requirements.city}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location_requirements: { ...prev.location_requirements, city: e.target.value },
                }))}
                placeholder="e.g., Vancouver"
              />
            </div>
          </div>
        </div>

        {/* Justification */}
        <div>
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Justification
            <span className="text-xs text-gray-500 font-normal">(Required)</span>
          </label>
          <textarea
            value={formData.justification}
            onChange={(e) => setFormData(prev => ({ ...prev, justification: e.target.value }))}
            placeholder="Explain why additional staff is needed, what gaps exist, and how this will help..."
            rows={4}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            type="submit"
            disabled={createRequestMutation.isPending}
            className="min-w-[120px]"
          >
            {createRequestMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}
